export interface SectionMeta {
  label: string;
  description: string;
  intro: string;
  /** Human, search-facing <title>/og:title. Falls back to the plain label. */
  pageTitle?: string;
}

export const DOCS_BASE = '/docs';

export const prettifySegment = (segment: string): string =>
  segment
    .replace(/^\d+-/, '')
    .replace(/-/g, ' ')
    .replace(/\bapi\b/gi, 'API')
    .replace(/\besg\b/gi, 'ESG')
    .replace(/\bkpis\b/gi, 'KPIs')
    .replace(/\bmcp\b/gi, 'MCP')
    .replace(/\bsdk\b/gi, 'SDK')
    .replace(/^./, (c) => c.toUpperCase());

export const SECTION_META: Record<string, SectionMeta> = {
  '': {
    label: 'AxionQuant Documentation',
    pageTitle: 'AxionQuant API Documentation',
    description:
      'Reference documentation for the AxionQuant API: market data, alternative data, company fundamentals, macros and signals, and client libraries. Start building with AxionQuant today.',
    intro:
      'Everything you need to build with AxionQuant: REST endpoints across market data, fundamentals, alternative data and macro signals, plus official client libraries for Python, JavaScript and C. Start with the introduction or jump straight to a section below.',
  },
  '00-overview': {
    label: 'Platform Overview',
    description:
      'Introduction to the AxionQuant API and getting-started guide: authentication, base URL, rate limits and your first request.',
    intro:
      'New here? These pages cover how the platform is structured, how authentication works, and how to make your first request in minutes.',
  },
  '01-market-data': {
    label: 'Market Data',
    description:
      'Real-time and historical market data APIs: stocks, ETFs, forex, futures, indices and crypto: quotes, ticker details and historical prices.',
    intro:
      'Quotes, ticker details and historical prices across equities, funds, currencies, derivatives and digital assets. Every asset class follows the same pattern: find tickers, pull details, fetch history.',
  },
  '01-market-data/ETFs': {
    label: 'ETFs & Funds',
    description:
      'ETF API endpoints: search funds and retrieve holdings, weights, exposure breakdowns, fund details, quotes and historical prices.',
    intro:
      'Search the ETF universe and drill into composition: holdings, weights and exposure breakdowns alongside fund metadata, quotes and price history.',
  },
  '01-market-data/crypto': {
    label: 'Crypto Markets',
    description:
      'Cryptocurrency endpoints: find tickers, quotes, ticker details and historical prices for coins and tokens.',
    intro:
      'Coverage for coins and tokens: discover tradable tickers, pull live quotes and details, and retrieve historical OHLCV data for backtesting.',
  },
  '01-market-data/forex': {
    label: 'Currencies & FX',
    description:
      'Foreign exchange endpoints: find currency pair tickers, quotes, pair details and historical exchange rates.',
    intro:
      'Currency pairs with quotes, pair metadata and historical exchange rates, suited to macro research and regional market analysis.',
  },
  '01-market-data/futures': {
    label: 'Futures Markets',
    description:
      'Futures market data endpoints: find contract tickers, quotes, contract details and historical prices, filterable by exchange.',
    intro:
      'Contract-level futures coverage across major exchanges: find tickers by product or exchange, inspect contract details and download historical prices.',
  },
  '01-market-data/indices': {
    label: 'Market Indices',
    description:
      'Market index endpoints: components, exposure, ticker search, quotes, index details and historical index prices.',
    intro:
      'Benchmark indices end to end: list constituents, measure exposure, and pull index-level quotes and history for benchmarking and research.',
  },
  '01-market-data/stocks': {
    label: 'Stocks & Equities',
    description:
      'Equity market data endpoints: find tickers, quotes, ticker details and historical prices for listed equities.',
    intro:
      'Core equity coverage: search the ticker universe, pull quote and reference data, and download historical prices adjusted for splits and dividends.',
  },
  '02-alternative-data': {
    label: 'Alternative Data',
    description:
      'Alternative data APIs: ESG scores, sentiment analysis, supply chain relationships and web traffic signals.',
    intro:
      'Datasets you will not find in a standard market feed: ESG scores, multi-source sentiment, supply chain graphs and website traffic used as a market signal.',
  },
  '02-alternative-data/sentiment': {
    label: 'Market Sentiment',
    description:
      'Sentiment score endpoints aggregating social media, news, journalist tone and analyst commentary for any ticker.',
    intro:
      'Ticker-level sentiment from four sources; social media, news flow, journalist tone and analyst commentary; so you can test which signal carries alpha for your strategy.',
  },
  '02-alternative-data/supply-chain': {
    label: 'Supply Chain Relationships',
    description:
      'Supply chain relationship endpoints: map customers, suppliers and peers for any public company.',
    intro:
      'The corporate relationship graph: customers, suppliers and peers per company, ready for contagion analysis and beta correlations across the supply chain.',
  },
  '03-fundamentals': {
    label: 'Company Fundamentals',
    description:
      'Company fundamentals APIs: corporate intelligence, earnings, SEC filings, financial statements and insider activity.',
    intro:
      'The fundamental layer behind long-horizon research: corporate intelligence, earnings history and transcripts, filings, statement-level financials and insider transactions.',
  },
  '03-fundamentals/corp.-intelligence': {
    label: 'Corporate Intelligence',
    description:
      'Corporate intelligence endpoints: company profiles, snapshots, KPIs, analyst ratings, key dates and operating information.',
    intro:
      'A company in one place: profiles, snapshot metrics, KPIs, analyst ratings, key dates and operating details for screening and entity enrichment.',
  },
  '03-fundamentals/earnings': {
    label: 'Earnings & Transcripts',
    description:
      'Earnings endpoints: call transcripts, reported results, earnings history and estimate trends.',
    intro:
      'Earnings intelligence: transcripts, reported numbers, multi-period history and trend views of estimates, everything post-event analysis needs.',
  },
  '03-fundamentals/filings': {
    label: 'SEC Filings & Disclosures',
    description:
      'SEC filing endpoints: corporate filings, full-text search, document retrieval and form-type listings.',
    intro:
      'Primary-source disclosure: search filings by company and form type, retrieve documents, and track corporate filings as they land.',
  },
  '03-fundamentals/financials': {
    label: 'Financial Statements & Metrics',
    description:
      'Financial statement endpoints: snapshots, calculated metrics, statements, DCF valuation, historical valuation ratios and time series.',
    intro:
      'Statement-level financials plus derived analytics: snapshots, calculated metrics, DCF valuation and historical valuation ratios, normalised for cross-company comparison.',
  },
  '03-fundamentals/financials/series': {
    label: 'Financial Time Series',
    description:
      'Financial time series endpoints: assets, cash flow, equity, income statement, liabilities, revenue and shares outstanding over time.',
    intro:
      'Line-item history as clean time series: assets, liabilities, equity, revenue, income, cash flow and share counts, ready for modelling.',
  },
  '03-fundamentals/insiders': {
    label: 'Insiders & Ownership',
    description:
      'Insider endpoints: activity summaries, fund holders, individuals, institutions, major holders and transaction history.',
    intro:
      'Follow the money inside the company: individual and institutional holders, fund ownership, major-holder breakdowns and the full transaction record.',
  },
  '04-macros-and-signals': {
    label: 'Macros & Signals',
    description:
      'Macro and event-driven APIs: economic and corporate calendars, credit ratings, economic datasets and news feeds.',
    intro:
      'Context that moves markets: economic and corporate event calendars, credit ratings, curated macro datasets and news feeds you can filter down to a sector.',
  },
  '04-macros-and-signals/calendar': {
    label: 'Events & Calendars',
    description:
      'Calendar endpoints: corporate events such as earnings dates and IPOs, plus the macroeconomic calendar.',
    intro:
      'Time your models around reality: corporate events including earnings dates and listings, alongside the macroeconomic calendar.',
  },
  '04-macros-and-signals/credit-ratings': {
    label: 'Credit Ratings & Risk',
    description:
      'Credit rating endpoints: search rated entities and retrieve their rating reports.',
    intro:
      'Search credit-rated entities and pull their rating reports for risk screens and fixed-income research.',
  },
  '04-macros-and-signals/economic-datasets': {
    label: 'Economic Indicators & Data',
    description:
      'Economic data endpoints: search curated macro datasets and retrieve their observations.',
    intro:
      'Curated macro series: search datasets by theme and retrieve observation histories aligned to your backtest window.',
  },
  '04-macros-and-signals/news': {
    label: 'News & Media Feeds',
    description:
      'News endpoints: corporate news, global news, a live feed and sector-filtered coverage.',
    intro:
      'News built for machines: global and per-company coverage, a live feed and sector filters; each article tagged for entity and impact analysis.',
  },
  'client-libraries': {
    label: 'Client Libraries',
    description:
      'Official AxionQuant client libraries: Python SDK, JavaScript/TypeScript SDK and C SDK.',
    intro:
      'Official SDKs so you skip the HTTP plumbing: Python, JavaScript/TypeScript and a lightweight C library for embedded and HFT work.',
  },
  'client-libraries/python-SDK': {
    label: 'Python SDK',
    description:
      'The AxionQuant Python SDK: core client, models, technical analysis, utilities and visualisation modules.',
    intro:
      'The axionquant-sdk package: a pandas-friendly core client plus modules for models, technical analysis, utilities and Plotly visualisations.',
  },
};

/**
 * Human-friendly names for individual doc pages ("spokes"), used by the
 * section-hub grids. Keys are doc paths relative to /docs. Falls back to
 * SECTION_META labels for nested hubs, then prettified file names.
 */
export const DOC_LABELS: Record<string, string> = {
  // 00-overview
  '00-overview/00-introduction': 'Introduction',
  '00-overview/01-getting-started': 'Getting Started',

  // ETFs
  '01-market-data/ETFs/list': 'Browse the ETF Universe',
  '01-market-data/ETFs/00-holdings': 'ETF Holdings',
  '01-market-data/ETFs/01-weights': 'Holdings Weights',
  '01-market-data/ETFs/02-exposure': 'Sector & Asset Exposure',
  '01-market-data/ETFs/03-find-tickers': 'Search ETF Tickers',
  '01-market-data/ETFs/04-quote': 'Live ETF Quotes',
  '01-market-data/ETFs/05-fund-details': 'Fund Profiles',
  '01-market-data/ETFs/06-ticker-details': 'Ticker Reference Data',
  '01-market-data/ETFs/historical-prices': 'ETF Price History',

  // Crypto
  '01-market-data/crypto/list': 'Browse Listed Coins & Tokens',
  '01-market-data/crypto/00-find-tickers': 'Search Crypto Tickers',
  '01-market-data/crypto/01-quote': 'Live Crypto Quotes',
  '01-market-data/crypto/02-ticker-details': 'Coin & Token Profiles',
  '01-market-data/crypto/03-historical-prices': 'Crypto Price History',

  // Forex
  '01-market-data/forex/list': 'Browse Currency Pairs',
  '01-market-data/forex/00-find-tickers': 'Search Currency Pairs',
  '01-market-data/forex/01-quote': 'Live FX Quotes',
  '01-market-data/forex/02-ticker-details': 'Currency Pair Profiles',
  '01-market-data/forex/03-historical-prices': 'Exchange Rate History',

  // Futures
  '01-market-data/futures/list': 'Browse Futures Contracts',
  '01-market-data/futures/00-find-tickers': 'Search Futures Contracts',
  '01-market-data/futures/01-quote': 'Live Futures Quotes',
  '01-market-data/futures/02-historical-prices': 'Futures Price History',
  '01-market-data/futures/03-ticker-details': 'Contract Specifications',

  // Indices
  '01-market-data/indices/list': 'Browse Market Indices',
  '01-market-data/indices/00-components': 'Index Constituents',
  '01-market-data/indices/01-exposure': 'Index Factor Exposure',
  '01-market-data/indices/02-find-tickers': 'Search Index Tickers',
  '01-market-data/indices/03-quote': 'Live Index Quotes',
  '01-market-data/indices/04-ticker-details': 'Index Profiles',
  '01-market-data/indices/05-historical-prices': 'Index Price History',

  // Stocks
  '01-market-data/stocks/list': 'Browse the Equity Universe',
  '01-market-data/stocks/00-find-tickers': 'Search Stock Tickers',
  '01-market-data/stocks/01-quote': 'Live Stock Quotes',
  '01-market-data/stocks/02-ticker-details': 'Company Reference Data',
  '01-market-data/stocks/03-historical-prices': 'Stock Price History',

  // Alternative data
  '02-alternative-data/ESG': 'ESG Scores',
  '02-alternative-data/web-traffic': 'Web Traffic Signals',
  '02-alternative-data/sentiment/social-media': 'Social Media Sentiment',
  '02-alternative-data/sentiment/analysts': 'Analyst Tone',
  '02-alternative-data/sentiment/journalists': 'Journalist Coverage Tone',
  '02-alternative-data/supply-chain/customers': 'Customer Relationships',
  '02-alternative-data/supply-chain/suppliers': 'Supplier Relationships',
  '02-alternative-data/supply-chain/peers': 'Competitor Peers',

  // Corporate intelligence
  '03-fundamentals/corp.-intelligence/00-profile': 'Company Profiles',
  '03-fundamentals/corp.-intelligence/01-snapshot': 'At-a-Glance Snapshot',
  '03-fundamentals/corp.-intelligence/KPIs': 'Key Performance Indicators',
  '03-fundamentals/corp.-intelligence/analyst-ratings': 'Street Analyst Ratings',
  '03-fundamentals/corp.-intelligence/key-dates': 'Fiscal & Corporate Key Dates',
  '03-fundamentals/corp.-intelligence/operating-info': 'Operating Information',

  // Earnings
  '03-fundamentals/earnings/00-transcripts': 'Earnings Call Transcripts',
  '03-fundamentals/earnings/01-report': 'Reported Results',
  '03-fundamentals/earnings/02-history': 'Earnings History',
  '03-fundamentals/earnings/03-trend': 'Estimates & Surprise Trends',

  // Filings
  '03-fundamentals/filings/00-corporate': 'Corporate Filings Feed',
  '03-fundamentals/filings/01-search': 'Full-Text Filing Search',
  '03-fundamentals/filings/02-document': 'Fetch Filing Documents',
  '03-fundamentals/filings/list-forms': 'Browse by Form Type',

  // Financials
  '03-fundamentals/financials/00-snapshot': 'Financial Snapshot',
  '03-fundamentals/financials/01-metrics': 'Calculated Ratios & Metrics',
  '03-fundamentals/financials/02-statements': 'Full Financial Statements',
  '03-fundamentals/financials/03-discount-cash-flow': 'DCF Valuation Model',
  '03-fundamentals/financials/04-historical-valuation': 'Historical Valuation Ratios',

  // Financial time series
  '03-fundamentals/financials/series/revenue': 'Revenue Over Time',
  '03-fundamentals/financials/series/income': 'Income Statement Series',
  '03-fundamentals/financials/series/cashflow': 'Cash Flow Series',
  '03-fundamentals/financials/series/assets': 'Total Assets Series',
  '03-fundamentals/financials/series/liabilities': 'Liabilities Series',
  '03-fundamentals/financials/series/equities': "Shareholders' Equity Series",
  '03-fundamentals/financials/series/shares': 'Shares Outstanding Series',

  // Insiders
  '03-fundamentals/insiders/activity': 'Insider Activity Feed',
  '03-fundamentals/insiders/major-holders': 'Major Holders Breakdown',
  '03-fundamentals/insiders/institutions': 'Institutional Ownership',
  '03-fundamentals/insiders/funds': 'Fund Ownership',
  '03-fundamentals/insiders/individuals': 'Individual Insiders',
  '03-fundamentals/insiders/transactions': 'Insider Transaction History',

  // Calendars
  '04-macros-and-signals/calendar/corporate-events': 'Corporate Events Calendar',
  '04-macros-and-signals/calendar/economic-calendar': 'Economic Release Calendar',

  // Credit ratings
  '04-macros-and-signals/credit-ratings/00-search': 'Find Rated Entities',
  '04-macros-and-signals/credit-ratings/report': 'Rating Reports',

  // Economic datasets
  '04-macros-and-signals/economic-datasets/00-search': 'Search Macro Datasets',
  '04-macros-and-signals/economic-datasets/data': 'Dataset Observations',

  // News
  '04-macros-and-signals/news/live-feed': 'Real-Time News Feed',
  '04-macros-and-signals/news/global': 'Global News Coverage',
  '04-macros-and-signals/news/corporate': 'Company News',
  '04-macros-and-signals/news/sector': 'Sector News Filters',

  // Client libraries
  'client-libraries/C-SDK': 'C SDK: Embedded & HFT',
  'client-libraries/javascript-SDK': 'JavaScript / TypeScript SDK',
  'client-libraries/python-SDK/01-core': 'Core Client & Namespaces',
  'client-libraries/python-SDK/models': 'Regression & Forecasting Models',
  'client-libraries/python-SDK/technical-analysis': 'Technical Indicators (TA)',
  'client-libraries/python-SDK/utilities-and-helpers': 'Utilities & Helpers',
  'client-libraries/python-SDK/visualizations': 'Plotly Visualisations',
};

export const getDocLabel = (docPath: string): string =>
  DOC_LABELS[docPath] ?? SECTION_META[docPath]?.label ?? prettifySegment(docPath.split('/').pop() ?? '');

/**
 * Human, search-facing page titles per hub. These feed <title>/og:title via
 * getSectionMeta().sectionMeta.pageTitle, keeping the short `label`s for
 * navigation, cards and on-page headings.
 */
const PAGE_TITLES: Record<string, string> = {
  '': 'API Documentation: Guides, Endpoints & SDKs',
  '00-overview': 'API Overview & Getting Started',
  '01-market-data': 'Market Data APIs: Stocks, ETFs, Forex & More',
  '01-market-data/ETFs': 'ETF Data API: Holdings, Weights & Exposure',
  '01-market-data/crypto': 'Crypto Data API: Coins, Tokens & Price History',
  '01-market-data/forex': 'Forex Data API: Currency Pairs & Exchange Rates',
  '01-market-data/futures': 'Futures Data API: Contracts & Price History',
  '01-market-data/indices': 'Index Data API: Constituents, Exposure & History',
  '01-market-data/stocks': 'Stock Data API: Quotes & Price History',
  '02-alternative-data': 'Alternative Data: Sentiment, ESG & More',
  '02-alternative-data/sentiment': 'Sentiment Data: Social, News & Analysts',
  '02-alternative-data/supply-chain': 'Supply Chain Data: Customers, Suppliers & Peers',
  '03-fundamentals': 'Fundamentals: Financials, Filings & Earnings',
  '03-fundamentals/corp.-intelligence': 'Corporate Intelligence: Profiles & KPIs',
  '03-fundamentals/earnings': 'Earnings Data: Transcripts, Reports & Trends',
  '03-fundamentals/filings': 'SEC Filings: Search & Track Disclosures',
  '03-fundamentals/financials': 'Financial Statements: Snapshots to DCF',
  '03-fundamentals/financials/series': 'Financial Time Series: Revenue & Cash Flow',
  '03-fundamentals/insiders': 'Insider Activity: Holders & Transactions',
  '04-macros-and-signals': 'Macros & Signals: Calendars, Ratings & News',
  '04-macros-and-signals/calendar': 'Calendars: Corporate Events & Economic Releases',
  '04-macros-and-signals/credit-ratings': 'Credit Ratings: Entities & Reports',
  '04-macros-and-signals/economic-datasets': 'Economic Datasets: Macro Series & Data',
  '04-macros-and-signals/news': 'News APIs: Global, Company & Sector Coverage',
  'client-libraries': 'Client Libraries: Python, JavaScript & C SDKs',
  'client-libraries/python-SDK': 'Python SDK: Client, Models & Analysis',
};

export const sectionUrl = (sectionPath: string): string =>
  sectionPath ? `${DOCS_BASE}/${sectionPath}` : DOCS_BASE;

export const getSectionMeta = (sectionPath: string): SectionMeta => {
  const fallback: SectionMeta = {
    label: prettifySegment(sectionPath.split('/').pop() ?? 'Documentation'),
    description: SECTION_META[''].description,
    intro: '',
  };
  const meta = SECTION_META[sectionPath] ?? fallback;
  return { ...meta, pageTitle: PAGE_TITLES[sectionPath] ?? meta.label };
};
